import { Alert } from 'antd';
import React, { useCallback, useMemo, useState } from 'react';

import { IDatability } from '@mono/types/src/weather-widgets';
import { toHash } from '@mono/to-hash';

import UnitSelector from '../UnitSelector';
import CapabilityFormGroup from './CapabilityFormGroup';
import DatasetFormGroup, { MinimalDataset } from './DatasetFormGroup';
import InterpolationIntervalFormGroup from './InterpolationIntervalFormGroup';
import RadiusFormGroup from './RadiusFormGroup';

interface DatabilityFormGroupProps<Datability extends IDatability = IDatability>
    extends Pick<React.ComponentProps<typeof UnitSelector>, 'unitLabel' | 'onUnitLabelChange'> {
    datability: Datability;
    passedDownDatasets?: MinimalDataset[];
    unit?: string;
    onUnitChange?({ datability, unit }: { datability: Datability; unit: string }): void;
    onChangeDatability(datability: Datability): void;
    dataType: string[];
    vectorNormalization?: 'magnitude' | 'direction';
    onVectorNormalizationChange?(value: string): void;
    onInterpolationChange(interval: string): void;
    disableInterpolation?: boolean;
    dataStructures?: string[];
    mimeTypes?: string[];
    maxRadiusKm?: number;
    directionalDatability?: Datability;
}

const DatabilityFormGroup = ({
    datability,
    passedDownDatasets,
    unit,
    unitLabel,
    onUnitChange,
    onUnitLabelChange,
    onChangeDatability,
    dataType,
    vectorNormalization,
    onVectorNormalizationChange,
    onInterpolationChange,
    disableInterpolation,
    dataStructures = ['timeseries/point2d'],
    mimeTypes,
    maxRadiusKm,
}: DatabilityFormGroupProps) => {
    const [loadedDatasetIds, setLoadedAvailableDatasetIds] = useState<string[]>();
    const setDatasetId = useCallback(
        (datasetId?: string) => {
            if (datability.datasetId !== datasetId) {
                onChangeDatability({ ...datability, datasetId });
            }
        },
        [toHash(datability), onChangeDatability]
    );

    const onCapabilityUpdate = (capabilityId: string | undefined) => {
        if (datability.capabilityId !== capabilityId) {
            onChangeDatability({ ...datability, capabilityId });
        }
    };

    const onChangeUnit = useMemo(
        () =>
            onUnitChange
                ? (unit: string) => {
                      onUnitChange({ datability, unit });
                  }
                : undefined,
        [toHash(datability), onUnitChange]
    );

    return (
        <>
            <Alert style={{ gridColumn: '1/6' }} message='You can start by selecting a dataset or a capability first' type='info' />
            <DatasetFormGroup
                passedDownDatasets={passedDownDatasets}
                datability={datability}
                mimeTypes={mimeTypes}
                setDatasetId={setDatasetId}
                dataType={dataType}
                setLoadedAvailableDatasetIds={setLoadedAvailableDatasetIds}
                dataStructures={dataStructures}
            />
            {datability.space?.type === 'Point' && (
                <RadiusFormGroup
                    radius={datability._radius ?? 1e3}
                    maxRadiusKm={maxRadiusKm}
                    setRadius={(_radius) => onChangeDatability({ ...datability, _radius })}
                />
            )}
            <InterpolationIntervalFormGroup disabled={!!disableInterpolation} label='Interpolation' datability={datability} onChange={onInterpolationChange} />
            <CapabilityFormGroup
                unit={unit}
                onCapabilityChange={onCapabilityUpdate}
                onUnitChange={onChangeUnit}
                onUnitLabelChange={onUnitLabelChange}
                unitLabel={unitLabel ? unitLabel : unit}
                datability={datability}
                loadedDatasetIds={loadedDatasetIds}
                dataType={dataType}
                vectorNormalization={vectorNormalization}
                onVectorNormalizationChange={onVectorNormalizationChange}
                passedDownDatasets={passedDownDatasets}
            />
        </>
    );
};

export default React.memo(DatabilityFormGroup);
